import { useEffect, useState } from "react";
import { message } from "antd";
import request from "project-config/services/request/request";
import { useTranslation } from "react-i18next";
import { dateFormatter } from "project-config/utils/date-formatter";
import {
  InvoicingStatus,
  InvoicingType,
  InvoiceTypeEnum,
  Whether,
} from "../constants";
import { useInvoiceStore } from "../store/invoice-store";

interface IInvoiceDetailInfo {
  invoiceOrderNumber: string;
  createDate: number;
  athensMode: number;
  status: number;
  rechargeOrderNumber: string;
  money: number;
  titleType: number;
  invoiceTitle: string;
  changHeadEnterprise: number;
  taxPayIdentifyNumber: string;
  resistAddress: string;
  fixTelephone: string;
  bankAccountNumber: string;
  bankDeposit: string;
  bankName: string;
  bankNo: string;
  invoiceRemark: string;
  emailAddress: string;
  mailAddress: string;
  recipe: string;
  contactNumber: string;
  issueCompany: string;
  invoiceType: number;
  electronInvoiceUrls: string[] | null;
}

interface IIvoiceDetailContent {
  text: string;
  value: string | null | string[] | number;
  isShowChangeInvoiceHeader?: boolean;
  isHidden?: boolean;
  showAppendixList?: boolean;
  disableText?: boolean;
  showImgUrl?: boolean;
}

export interface IInvoiceDetailFormInfo {
  title: string;
  showHeaderIcon?: boolean;
  rechargeOrderNumberList?: string[];
  content: IIvoiceDetailContent[];
}

export function useInitInvoiceDetail() {
  const { t } = useTranslation("invoice");
  const [invoiceDetailInfo, setInvoiceDetailInfo] = useState<
    IInvoiceDetailFormInfo[]
  >([]);
  const { setOpenDrawer, openDrawer, currentinvoiceOrderNumber } =
    useInvoiceStore();

  const handleRevokeRecord = async (invoiceOrderNumber: string) => {
    const { code } = await request.post<IInvoiceDetailInfo>(
      "/api/rendering/user/cancelInvoice",
      { invoiceOrderNumber }
    );
    if (code === 200) {
      message.success(t("invoice_revoke_success_tip"));
      return;
    }
    message.error(t("invoice_revoke_fail_tip"));
  };

  useEffect(() => {
    if (!openDrawer) return;
    const fetchInvoiceDetail = async (invoiceOrderNumber: string) => {
      const { data } = await request.post<IInvoiceDetailInfo>(
        "/api/rendering/user/invoiceDetail",
        { invoiceOrderNumber }
      );
      if (!data) return;
      const result = [
        {
          title: t("invoice_detail_basic_information"),
          content: [
            {
              text: t("invoice_detail_orderno"),
              value: data.invoiceOrderNumber,
            },
            {
              text: t("invoice_detail_applytime"),
              value: dateFormatter(data.createDate),
            },
            {
              text: t("invoice_detail_status"),
              value: t(`invoice_invoice_status_${data.status}`),
              showImgUrl: data.status === InvoicingStatus.Billed,
              disableText: data.status === InvoicingStatus.OutOfDate,
            },
            {
              text: t("invoice_detail_billing_company"),
              value: data.issueCompany,
            },
          ],
        },
        {
          title: t("invoice_detail_link_orderno"),
          showHeaderIcon: true,
          rechargeOrderNumberList: data.rechargeOrderNumber
            .split(";")
            .map((item) => item),
          content: [
            {
              text: t("invoice_detail_orderno_amount"),
              value: data.money,
            },
            {
              text: t("invoice_detail_orderno_num"),
              value: data.rechargeOrderNumber
                .split(";")
                .map((item: string) => item).length,
            },
          ],
        },
        {
          title: t("add_invoice_invoice_info"),
          content: [
            {
              text: t("invoice_detail_invoice"),
              value: data.invoiceTitle,
              isShowChangeInvoiceHeader:
                data.changHeadEnterprise === Whether.Yes,
            },
            {
              text: t("user_invoiceReceipt_recognition"),
              value: data.taxPayIdentifyNumber,
              isHidden: data.titleType === InvoicingType.Personal,
            },
            {
              text: t("user_com_invoiceRe_label_companyAddr"),
              value: data.resistAddress,
              isHidden: data.invoiceType !== InvoiceTypeEnum.PaperVAT,
            },
            {
              text: t("user_com_invoiceRe_label_companyTel"),
              value: data.fixTelephone,
              isHidden: data.invoiceType !== InvoiceTypeEnum.PaperVAT,
            },
            {
              text: t("user_invoiceReceipt_head_bank_account"),
              value: data.bankAccountNumber,
              isHidden: data.invoiceType !== InvoiceTypeEnum.PaperVAT,
            },
            {
              text: t("user_invoiceReceipt_head_bank"),
              value: data.bankDeposit,
              isHidden: data.invoiceType !== InvoiceTypeEnum.PaperVAT,
            },
            {
              text: t("invoice_detail_bankname"),
              value: data.bankName,
              isHidden: !data.bankName,
            },
            {
              text: t("invoice_detail_bankno"),
              value: data.bankNo,
              isHidden: !data.bankNo,
            },
            {
              text: t("invoice_detail_remark"),
              value: data.invoiceRemark,
            },
          ],
        },
        {
          title: t("invoice_detail_receiving_method"),
          content: [
            {
              text: t("user_com_addAddr_label_name"),
              value: data.recipe,
              isHidden:
                data.invoiceType !== InvoiceTypeEnum.PaperVAT ||
                !data.mailAddress,
            },
            {
              text: t("user_com_addAddr_label_user_tel"),
              value: data.contactNumber,
              isHidden:
                data.invoiceType !== InvoiceTypeEnum.PaperVAT ||
                !data.mailAddress,
            },
            {
              text: t("user_com_addAddr_label_addr"),
              value: data.mailAddress,
              isHidden:
                data.invoiceType !== InvoiceTypeEnum.PaperVAT ||
                !data.mailAddress,
            },
            {
              text: t("invoice_detail_email"),
              value: data.emailAddress,
              isHidden: !data.emailAddress,
            },
            {
              text: t("invoice_detail_appendix"),
              value: data.electronInvoiceUrls,
              showAppendixList: true,
            },
          ],
        },
      ];
      setInvoiceDetailInfo(
        result.map((item) => {
          return {
            title: item.title,
            rechargeOrderNumberList: item.rechargeOrderNumberList,
            content: (item.content as IIvoiceDetailContent[]).filter(
              (el: IIvoiceDetailContent) => !el.isHidden
            ),
            showHeaderIcon: item.showHeaderIcon,
          };
        })
      );
    };
    fetchInvoiceDetail(currentinvoiceOrderNumber);
  }, [openDrawer, currentinvoiceOrderNumber, t, setOpenDrawer]);

  return { invoiceDetailInfo, handleRevokeRecord };
}
